/**
 * Tuning constants for the whole game. Pure data: no imports, no side effects.
 *
 * The server may override some of these from environment variables (see
 * `apps/server/src/env.ts`). Rule functions accept a config object so tests can
 * pass a modified copy without touching the global default.
 */

export interface GameConfig {
  /** Server game-logic tick, in milliseconds. */
  TICK_MS: number;
  /** State patch broadcast interval, in milliseconds. */
  PATCH_RATE_MS: number;
  /** Multiplies `dt` for every game timer. `60` makes 10 min become 10 s. */
  TIME_SCALE: number;

  /** Maximum exhaustion, expressed in seconds of work-equivalent. */
  MAX_EXHAUSTION_S: number;
  /** Exhaustion-seconds gained per second worked. */
  WORK_EXHAUSTION_RATE: number;
  /** Exhaustion-seconds recovered per second drinking coffee. */
  COFFEE_RECOVERY_RATE: number;
  /** Exhaustion-seconds recovered per second while idle. */
  IDLE_RECOVERY_RATE: number;
  /** Exhaustion level at which `burnedOut` clears after idle, coffee or offline recovery. */
  BURNOUT_CLEAR_THRESHOLD: number;

  /** Nominal developer count, used only to size projects. */
  DEVELOPERS_NOMINAL: number;
  /** Nominal work cycles, used only to size projects. */
  CYCLES_NOMINAL: number;
  /** Total work-units required to ship a project. */
  PROJECT_REQUIRED_UNITS: number;
  /** Duration of the celebration/results phase, in milliseconds. */
  CELEBRATION_MS: number;

  /** Hard cap on connected clients (including reconnection grace seats). */
  MAX_CLIENTS: number;
  /** Reconnection grace window, in seconds. */
  RECONNECT_GRACE_S: number;

  /** Local player speed in pixels per second. */
  PLAYER_SPEED_PX_S: number;
  /** Distance from a desk interaction point at which work can start. */
  INTERACT_RADIUS_PX: number;

  /** Maximum chat message length. */
  CHAT_MAX_LEN: number;
  /** Number of chat messages retained and replayed on join. */
  CHAT_HISTORY: number;
  /** Chat token-bucket burst size. */
  CHAT_RATE_BURST: number;
  /** Chat token-bucket refill, tokens per second. */
  CHAT_RATE_REFILL_PER_S: number;

  /** Minimum gap between emotes from one client, in milliseconds. */
  EMOTE_COOLDOWN_MS: number;
  /** Maximum accepted move messages per second from one client. */
  MOVE_MSG_MAX_HZ: number;
  /** How long an emote is shown above an avatar, in milliseconds. */
  EMOTE_DURATION_MS: number;
  /** How long a speech bubble is shown above an avatar, in milliseconds. */
  BUBBLE_DURATION_MS: number;

  /** Minimum gap between radio commands from one player, in milliseconds. */
  RADIO_COOLDOWN_MS: number;
  /** Per-mirror timeout when the server resolves a station, in milliseconds. */
  RADIO_RESOLVE_TIMEOUT_MS: number;
  /** Radio volume floor far from the hi-fi (0–1). */
  RADIO_MIN_GAIN: number;
  /** Distance from the hi-fi over which the radio fades to its floor, in pixels. */
  RADIO_FALLOFF_PX: number;
}

export const CONFIG: GameConfig = {
  TICK_MS: 100,
  PATCH_RATE_MS: 50,
  TIME_SCALE: 1,

  MAX_EXHAUSTION_S: 600,
  WORK_EXHAUSTION_RATE: 1.0,
  COFFEE_RECOVERY_RATE: 5.0,
  IDLE_RECOVERY_RATE: 0.5,
  BURNOUT_CLEAR_THRESHOLD: 120, // Resume work at 20% exhaustion; full recovery is optional.

  DEVELOPERS_NOMINAL: 8,
  CYCLES_NOMINAL: 10,
  PROJECT_REQUIRED_UNITS: 8 * 10 * 600,
  CELEBRATION_MS: 15_000,

  MAX_CLIENTS: 16,
  RECONNECT_GRACE_S: 30,

  PLAYER_SPEED_PX_S: 96,
  INTERACT_RADIUS_PX: 20,

  CHAT_MAX_LEN: 200,
  CHAT_HISTORY: 50,
  CHAT_RATE_BURST: 5,
  CHAT_RATE_REFILL_PER_S: 1,

  EMOTE_COOLDOWN_MS: 1000,
  MOVE_MSG_MAX_HZ: 30,
  EMOTE_DURATION_MS: 3000,
  BUBBLE_DURATION_MS: 5000,

  RADIO_COOLDOWN_MS: 3000,
  RADIO_RESOLVE_TIMEOUT_MS: 6000,
  RADIO_MIN_GAIN: 0.15,
  RADIO_FALLOFF_PX: 320,
};

/** Create a config by overriding parts of {@link CONFIG}. */
export function makeConfig(overrides: Partial<GameConfig> = {}): GameConfig {
  return { ...CONFIG, ...overrides };
}
